import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DtcValueListComponent } from '../../../lists/dtc/dtc-value-list/dtc-value-list.component';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ErrorMessageApiService } from '../../../../services/api/error-message-api.service';
import { CreateErrorMessageDto } from '../../../../../shared/models/error-messages/create-error-message-dto';
import { Router } from '@angular/router';
import { SelectionComponent } from '../../../common/selection/selection.component';
import { SelectionOptionValue } from '../../../../../shared/models/internal/selection-option-value';
import { TextAreaComponent } from '../../../common/text-area/text-area.component';
import { ErrorMessage } from '../../../../../shared/models/error-message/error-message';
import { FloppyIconComponent } from '../../../icons/floppy-icon/floppy-icon.component';
import { SwPartNumberService } from '../../../../services/sw-part-number.service';
import { LabelWithHelp } from '../../../common/label-with-help/label-with-help';
import { errorToString } from '../../../../common/common-utils';
import { EntityType } from '../../../../../shared/enums/entity-type';
import { PropagateCreatedEntityModal } from '../../../modals/propagation-modal/propagate-created-entity-modal';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { HotToastService } from '@ngxpert/hot-toast';
@Component({
  selector: 'app-new-error-message',
  imports: [
    DtcValueListComponent,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SelectionComponent,
    TextAreaComponent,
    FloppyIconComponent,
    LabelWithHelp,
    PropagateCreatedEntityModal,
    AgentActionDirective,
  ],
  templateUrl: './new-error-message.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './new-error-message.component.scss',
})
export class NewErrorMessageComponent implements OnInit {
  @Output() public createErrorMessageTriggered = new EventEmitter<ErrorMessage>();
  @Input() public navigateToErrorMessageOverview = true;
  @ViewChild('propagateCreatedEntityModal') public propagateCreatedEntityModal!: PropagateCreatedEntityModal;

  public swPartNumber = '';

  public headerOnTerminal!: FormControl;
  public detailsOnTerminal!: FormControl;
  public errorCategory!: FormControl;
  public internalNotes!: FormControl;
  public errorMessageForm!: FormGroup;
  public createErrorMessageDto: CreateErrorMessageDto = {} as CreateErrorMessageDto;
  public errorCategories: SelectionOptionValue[] = [{ id: '-1', selected: true, title: 'Select a category' }];

  private errorMessageApi = inject(ErrorMessageApiService);
  private toast = inject(HotToastService);
  private router = inject(Router);
  private swPartNumberService = inject(SwPartNumberService);

  public ngOnInit(): void {
    this.fetchErrorCategories();
    this.buildForm();
    this.swPartNumber = this.swPartNumberService.getSwPartNumber()?.displayId ?? '';
  }

  public createErrorMessage(): void {
    this.prepareErrorMessageDto();
    this.errorMessageApi.createErrorMessage(this.createErrorMessageDto).subscribe({
      next: (errorMessage) => {
        this.onErrorMessageCreated(errorMessage);
        const swPartNumberId = this.swPartNumberService.getSwPartNumberId();
        if (swPartNumberId) {
          this.propagateCreatedEntityModal.openModal(swPartNumberId, EntityType.ErrorMessage, errorMessage.id);
        }
      },
      error: (error) => {
        console.error(errorToString(error));
      },
    });
  }

  private buildForm(): void {
    this.headerOnTerminal = new FormControl(null, []);
    this.detailsOnTerminal = new FormControl(null, []);
    this.errorCategory = new FormControl(this.errorCategories[0].id, []);
    this.internalNotes = new FormControl(null, []);
    this.errorMessageForm = new FormGroup({
      headerOnTerminal: this.headerOnTerminal,
      detailsOnTerminal: this.detailsOnTerminal,
      errorCategory: this.errorCategory,
      internalNotes: this.internalNotes,
    });
  }

  private fetchErrorCategories(): void {
    this.errorMessageApi.getErrorCategories().subscribe({
      next: (errorCategories) => {
        this.errorCategories = [
          ...this.errorCategories,
          ...errorCategories.map((category) => {
            return { id: category, selected: false, title: category };
          }),
        ];
      },
      error: (error) => {
        console.error(errorToString(error));
      },
    });
  }

  private prepareErrorMessageDto(): void {
    this.createErrorMessageDto.headerOnTerminal = this.headerOnTerminal.value;
    this.createErrorMessageDto.detailsOnTerminal = this.detailsOnTerminal.value;
    this.createErrorMessageDto.category = this.errorCategory.value;
    this.createErrorMessageDto.internalNotes = this.internalNotes.value === '' ? null : this.internalNotes.value;
  }

  private onErrorMessageCreated(errorMessage: ErrorMessage): void {
    this.toast.success(`Created successfully error message: ${errorMessage.headerOnTerminal}`);
    this.createErrorMessageTriggered.emit(errorMessage);
  }
}
